/**
 * BOX型モーダルで共有する寸法・しきい値。
 *
 * BoxModal から切り出した定数。分割したサブコンポーネント同士が同じ値を見る必要が
 * あるため、値の持ち主をここ1箇所にする。
 */

import { BRANCH_CHOICE_LEAD_SEC } from '../branchChoiceTiming'

/**
 * 2カラム時に、縦型動画（最大478px）と読みやすい動画テキスト幅を両立できる最小幅。
 *
 * 768pxを境界にすると、iPad mini（768px）とiPad Air（820px）の縦向きでも
 * PCレイアウトになり、右パネルが約240〜290pxまで縮んで本文が細切れになる。
 * 900px未満は1カラムへ切り替え、横向きタブレットでは従来の2カラムを維持する。
 */
export const TWO_COLUMN_BREAKPOINT = 900

/**
 * 1カラム時に動画を object-fit: contain へ切り替える最小幅。
 *
 * 1カラムの動画ボックスは「幅100% × 残り高さ」なので、ビューポートが9:16より
 * 横長になるほど cover が横基準で拡大し、上下が切れる。実測値:
 *   iPad mini (768x1024) ボックス768x960  → 上下 29.7% が欠ける
 *   iPad Air  (820x1180) ボックス820x1116 → 上下 23.4% が欠ける
 *   iPhone SE (375x667)  ボックス375x603  → 上下  9.5%
 * 焼き込みテロップは下寄りに置かれるため、タブレット縦の欠けは文字が消える。
 * スマホは元々ビューポートがほぼ9:16で実害が無く、contain にすると黒帯だけが
 * 増えるので、タブレット以上の幅でのみ全体表示へ切り替える。
 */
export const VIDEO_CONTAIN_BREAKPOINT = 640

/** タイル型SPの詳細表示と同じ動画領域の高さ */
export const SP_DETAIL_VIDEO_HEIGHT = '24dvh'

/**
 * 動画下端のクレジット帯の高さ。シークバーはこの分だけ持ち上げ、足元にクレジットを敷く。
 * 動画の外に帯を作ると動画が小さくなるため、動画に重ねる方式にしている。
 */
export const CREDIT_STRIP_HEIGHT = 28

/**
 * 選択肢を自動で開く残り秒数。
 *
 * 焼き込みのテロップ・出演者クレジットは尺の中盤までに出し切るのが一般的なので、
 * 終盤に開けば映像の情報とぶつからない。押さない視聴者を取りこぼさないための保険でもある。
 */
export const CHOICE_AUTO_OPEN_REMAIN_SEC = BRANCH_CHOICE_LEAD_SEC

/** ピルの下端位置。シークバー（--moovy-seekbar-lift + 当たり判定）とクレジット帯を避ける */
export const CHOICE_PILL_BOTTOM = CREDIT_STRIP_HEIGHT + 26

/**
 * 決定エリア（設問＋選択肢）を、パネルの上から 35% の位置まで送る。
 *
 * `scrollIntoView({ block: 'nearest' })` は「最小限だけ動かす」挙動なので、選択肢が
 * 下端にぴったり張り付いて視線に入らなかった。かといって 'center' だと、選択肢が
 * 4つ以上あるときにブロックが縦に長くなり、見出しが上へはみ出す。
 * ブロックの上端を基準に送り、上には直前の本文を少し残して文脈を保つ。
 */
export const DECISION_FOCUS_RATIO = 0.35

/** 決定エリアをパネルの見やすい位置までスクロールさせる */
export const scrollDecisionIntoFocus = (container: HTMLDivElement, element: HTMLDivElement) => {
  // offsetTop は positioned な祖先が基準になり得るので、実測の差分で求める。
  const delta = element.getBoundingClientRect().top - container.getBoundingClientRect().top
  const top = container.scrollTop + delta - container.clientHeight * DECISION_FOCUS_RATIO
  container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
}
